// src/utils/totpUtils.js
import QRCode from 'qrcode';

// Browser-compatible TOTP implementation
// Using Web Crypto API instead of Node.js crypto module

// Configure TOTP settings
const TOTP_OPTIONS = {
  issuer: 'DP Duel App',
  algorithm: 'SHA-1',
  digits: 6,
  period: 30,
  window: 1 // Allow 1 time step tolerance
};

// Base32 alphabet for encoding/decoding
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/**
 * Generate a random base32 string for TOTP secret
 * @param {number} length - Length of the secret (default 32)
 * @returns {string} Base32 encoded secret
 */
export const generateTOTPSecret = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  let result = '';
  for (let i = 0; i < length; i++) {
    result += BASE32_ALPHABET[array[i] % 32];
  }
  return result;
};

/**
 * Decode base32 string to Uint8Array
 * @param {string} base32 - Base32 encoded string
 * @returns {Uint8Array} Decoded bytes
 */
const base32Decode = (base32) => {
  const input = base32.toUpperCase().replace(/[^A-Z2-7]/g, '');
  const output = new Uint8Array(Math.floor(input.length * 5 / 8));
  let bits = 0;
  let value = 0;
  let index = 0;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const charIndex = BASE32_ALPHABET.indexOf(char);
    if (charIndex === -1) continue;

    value = (value << 5) | charIndex;
    bits += 5;

    if (bits >= 8) {
      output[index++] = (value >>> (bits - 8)) & 0xFF;
      bits -= 8;
    }
  }

  return output;
};

/**
 * Generate HMAC-SHA1 using Web Crypto API
 * @param {Uint8Array} key - Secret key
 * @param {Uint8Array} data - Data to hash
 * @returns {Promise<Uint8Array>} HMAC result
 */
const hmacSha1 = async (key, data) => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);
  return new Uint8Array(signature);
};

/**
 * Generate TOTP code
 * @param {string} secret - Base32 encoded secret
 * @param {number} time - Unix timestamp (default: current time)
 * @returns {Promise<string>} 6-digit TOTP code
 */
const generateTOTPCode = async (secret, time = Math.floor(Date.now() / 1000)) => {
  const key = base32Decode(secret);
  let counter = Math.floor(time / TOTP_OPTIONS.period);
  
  // Convert counter to 8-byte big-endian array
  const counterArray = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    counterArray[i] = counter & 0xFF;
    counter >>>= 8;
  }
  
  const hmac = await hmacSha1(key, counterArray);
  const offset = hmac[hmac.length - 1] & 0x0F;
  const code = ((hmac[offset] & 0x7F) << 24) |
               ((hmac[offset + 1] & 0xFF) << 16) |
               ((hmac[offset + 2] & 0xFF) << 8) |
               (hmac[offset + 3] & 0xFF);
  
  return (code % Math.pow(10, TOTP_OPTIONS.digits)).toString().padStart(TOTP_OPTIONS.digits, '0');
};

/**
 * Generate a TOTP URI for QR code generation
 * @param {string} email - User's email address
 * @param {string} secret - TOTP secret
 * @returns {string} TOTP URI
 */
export const generateTOTPURI = (email, secret) => {
  const encodedIssuer = encodeURIComponent(TOTP_OPTIONS.issuer);
  const encodedAccount = encodeURIComponent(email);
  return `otpauth://totp/${encodedAccount}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=${TOTP_OPTIONS.digits}&period=${TOTP_OPTIONS.period}`;
};

/**
 * Generate QR code data URL for TOTP setup
 * @param {string} email - User's email address
 * @param {string} secret - TOTP secret
 * @returns {Promise<string>} QR code data URL
 */
export const generateTOTPQRCode = async (email, secret) => {
  const uri = generateTOTPURI(email, secret);
  console.log('TOTP URI:', uri);
  try {
    const qrCodeDataURL = await QRCode.toDataURL(uri, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'L',
      type: 'image/png'
    });
    console.log('QR Code generated successfully, length:', qrCodeDataURL.length);
    return qrCodeDataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Verify a TOTP token
 * @param {string} token - 6-digit token from authenticator app
 * @param {string} secret - User's TOTP secret
 * @returns {Promise<boolean>} Whether the token is valid
 */
export const verifyTOTPToken = async (token, secret) => {
  try {
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check current time window and previous/next windows for clock drift tolerance
    for (let i = -TOTP_OPTIONS.window; i <= TOTP_OPTIONS.window; i++) {
      const timeToCheck = currentTime + (i * TOTP_OPTIONS.period);
      const expectedCode = await generateTOTPCode(secret, timeToCheck);
      
      if (expectedCode === token) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error verifying TOTP token:', error);
    return false;
  }
};

/**
 * Generate a backup code for account recovery
 * @returns {string} 8-digit backup code
 */
export const generateBackupCode = () => {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
};

/**
 * Generate multiple backup codes
 * @param {number} count - Number of backup codes to generate
 * @returns {string[]} Array of backup codes
 */
export const generateBackupCodes = (count = 5) => {
  return Array.from({ length: count }, generateBackupCode);
};
